import { endPrfSession } from "./prf-session";
/**
 * WebAuthn PRF (Pseudo-Random Function) Extension
 *
 * This module handles WebAuthn credential registration and authentication
 * with the PRF extension for deriving encryption keys.
 *
 * The PRF extension allows us to derive deterministic encryption keys from
 * biometric authentication (Face ID / Touch ID), enabling passwordless
 * local backup encryption.
 *
 * IMPORTANT: PRF-encrypted backups should NEVER be exported - they're tied to
 * the specific passkey. Always use password encryption for backup exports.
 *
 * References:
 * - WebAuthn PRF explainer: https://github.com/w3c/webauthn/wiki/Explainer:-PRF-extension
 * - Yubico PRF guide: https://developers.yubico.com/WebAuthn/Concepts/PRF_Extension/Developers_Guide_to_PRF.html
 * - Bitwarden PRF: https://bitwarden.com/blog/prf-webauthn-and-its-role-in-passkeys/
 */

import {
	arrayBufferToBase64URL,
	base64ToArrayBuffer,
	base64URLToArrayBuffer,
	deriveEncryptionKeyFromPRF,
} from "./encryption";
import type {
	AuthenticationExtensionsClientInputsWithPRF,
	AuthenticationExtensionsClientOutputsWithPRF,
	PRFAuthResult,
	PRFCredentialClient,
} from "./types";

/**
 * Check if the browser supports WebAuthn PRF extension
 *
 * Note: This is a basic check. Actual PRF support depends on the authenticator.
 * We verify actual support during registration by checking the response.
 */
export function checkPRFSupport(): Promise<boolean> {
	return Promise.resolve(
		typeof window !== "undefined" &&
			Boolean(window.PublicKeyCredential) &&
			typeof navigator.credentials?.get === "function"
	);
}

/**
 * Authenticate with WebAuthn PRF to derive encryption key
 *
 * This performs a WebAuthn authentication ceremony with the PRF extension
 * to derive a deterministic encryption key from the user's passkey.
 *
 * @param credential - The credential to authenticate with (includes PRF salt)
 * @param challenge - Challenge from server
 * @param rpId - Relying Party ID
 * @returns PRF authentication result with derived encryption key
 */
let pendingAssertion: AbortController | null = null;

export function cancelPRFAuthentication(): void {
	endPrfSession();
	pendingAssertion?.abort();
	pendingAssertion = null;
}

export async function authenticateWithPRF(options: {
	credential?: PRFCredentialClient;
	credentials?: PRFCredentialClient[];
	challenge: ArrayBuffer;
	rpId: string;
}): Promise<PRFAuthResult> {
	pendingAssertion?.abort();
	const controller = new AbortController();
	pendingAssertion = controller;
	try {
		const credentials =
			options.credentials ?? (options.credential ? [options.credential] : []);
		if (!credentials.length) {
			return {
				success: false,
				error: "Enable backup unlock for a passkey first.",
			};
		}
		const prfExtension: AuthenticationExtensionsClientInputsWithPRF = {
			prf: {
				evalByCredential: Object.fromEntries(
					credentials.map((credential) => [
						arrayBufferToBase64URL(credential.credentialId),
						{ first: credential.prfSalt },
					])
				),
			},
		};
		// Perform WebAuthn authentication with PRF
		const assertion = (await navigator.credentials.get({
			signal: controller.signal,
			publicKey: {
				challenge: options.challenge,
				rpId: options.rpId,
				allowCredentials: credentials.map((credential) => ({
					id: credential.credentialId,
					type: "public-key" as const,
				})),
				userVerification: "required",
				timeout: 60_000,
				extensions: prfExtension as AuthenticationExtensionsClientInputs,
			},
		})) as PublicKeyCredential | null;

		if (controller.signal.aborted || !assertion) {
			return {
				success: false,
				cancelled: true,
				error: "Passkey unlock cancelled",
			};
		}

		if (
			!credentials.some(
				(credential) =>
					arrayBufferToBase64URL(credential.credentialId) ===
					arrayBufferToBase64URL(assertion.rawId)
			)
		) {
			return {
				success: false,
				error: "The selected passkey is not enabled for this backup.",
			};
		}

		// Get PRF results from extension outputs
		const extensionResults =
			assertion.getClientExtensionResults() as AuthenticationExtensionsClientOutputsWithPRF;

		if (!extensionResults.prf?.results?.first) {
			// PRF not supported or failed
			return {
				success: false,
				error:
					"This passkey cannot unlock backups in this browser. Use your backup password or restore a backup.",
			};
		}

		// Derive encryption key from PRF output
		const prfOutput = extensionResults.prf.results.first;
		const encryptionKey = await deriveEncryptionKeyFromPRF(prfOutput);

		return {
			success: true,
			encryptionKey,
			credentialId: arrayBufferToBase64URL(assertion.rawId),
		};
	} catch (error) {
		if (
			controller.signal.aborted ||
			(error instanceof Error &&
				["AbortError", "NotAllowedError"].includes(error.name))
		) {
			return {
				success: false,
				cancelled: true,
				error:
					"Passkey unavailable or cancelled. Use your backup password or choose another passkey.",
			};
		}
		return {
			success: false,
			error:
				"Passkey unlock did not complete. Use your backup password or try another passkey.",
		};
	} finally {
		if (pendingAssertion === controller) {
			pendingAssertion = null;
		}
	}
}

/**
 * Convert server-stored credential to client format
 */
export function toClientCredential(serverCredential: {
	credentialId: string;
	prfSalt: string;
	deviceName?: string;
}): PRFCredentialClient {
	return {
		credentialId: base64URLToArrayBuffer(serverCredential.credentialId),
		prfSalt: base64ToArrayBuffer(serverCredential.prfSalt),
		deviceName: serverCredential.deviceName,
	};
}

/**
 * Fresh challenge for client-only PRF evaluation. Better Auth owns sign-in
 * challenges and assertion verification; this ceremony only unwraps a key.
 */
export function generateChallenge(): ArrayBuffer {
	return crypto.getRandomValues(new Uint8Array(32)).buffer;
}
